import { Cancel01Icon } from '@hugeicons-pro/core-solid-rounded';
import { Delete02Icon, LicenseNoIcon, Settings01Icon } from '@hugeicons-pro/core-stroke-rounded';
import { useIsFocused, useScrollToTop } from 'expo-router/react-navigation';
import { FlashList } from '@shopify/flash-list';
import { InfiniteData, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'expo-router';
import React, { useCallback, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, RefreshControl } from 'react-native';

import {
  AlertDialog,
  AlertDialogBackdrop,
  AlertDialogBody,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
} from '@/components/gluestack/alert-dialog';
import { Box } from '@/components/gluestack/box';
import { Button, ButtonText } from '@/components/gluestack/button';
import { Heading } from '@/components/gluestack/heading';
import { HStack } from '@/components/gluestack/hstack';
import { Text } from '@/components/gluestack/text';
import { VStack } from '@/components/gluestack/vstack';
import { ScreenHeader } from '@/components/layout/ScreenHeader';
import { useSignalRUpdates } from '@/components/providers/SignalRProvider';
import { ButtonWrapper } from '@/components/ui/ButtonWrapper';
import { ErrorInListView } from '@/components/ui/ErrorInListView';
import { FloatingScrollButton } from '@/components/ui/FloatingScrollButton';
import { HeaderRefreshButton } from '@/components/ui/HeaderRefreshButton';
import { HugeIconsWrapper } from '@/components/ui/HugeIconsWrapper';
import { MessageView } from '@/components/ui/MessageView';
import { useInfiniteListUtils, createSkeletonItems } from '@/hooks/useInfiniteListUtils';
import { useIsLargeScreen } from '@/hooks/useIsLargeScreen';
import { useFlashListOptimizer } from '@/hooks/useFlashListOptimizer';
import {
  PageParam,
  getNextPageSkipOrCursor,
  buildPaginationParams,
} from '@/hooks/usePaginationUtils';
import { useToastMessages } from '@/hooks/useToastMessages';
import { PostsView } from '@/models/Post';
import { NotificationCard } from '~/components/domain/notifications/NotificationCard';
import { CardWrapper } from '~/components/domain/posts/CardWrapper';
import { PostsService } from '~/services/domain/PostsService';

export function NotificationsScreen() {
  const { t } = useTranslation();
  const { showSuccessToast, showErrorToast } = useToastMessages();
  const queryClient = useQueryClient();
  const isLargeScreen = useIsLargeScreen();
  const isScreenActive = useIsFocused();
  const flashListRef = useRef<React.ComponentRef<typeof FlashList<any>>>(null);
  const { onEndReachedThreshold, pageSize } = useFlashListOptimizer({
    estimatedRowsOnScreen: { mobile: 8, desktop: 10 },
  });
  const { notificationCount } = useSignalRUpdates();
  const router = useRouter();

  const [isScrolled, setIsScrolled] = useState(false);
  const [lastNotificationCount, setLastNotificationCount] = useState(notificationCount);
  const [showClearConfirmation, setShowClearConfirmation] = useState(false);
  const [isClearingNotifications, setIsClearingNotifications] = useState(false);
  const wasScreenActiveRef = useRef(isScreenActive);

  // Registers scroll to top for when the tab is pressed
  useScrollToTop(flashListRef);

  const { data, error, fetchNextPage, hasNextPage, isFetchingNextPage, isRefetching, isPending } =
    useInfiniteQuery({
      queryKey: ['notifications'],
      queryFn: async ({ pageParam }) => {
        return await PostsService.getNotifications(buildPaginationParams(pageParam, pageSize));
      },
      initialPageParam: undefined as PageParam,
      getNextPageParam: getNextPageSkipOrCursor,
      select: useCallback((data: InfiniteData<PostsView, PageParam>) => {
        const items = data.pages.flatMap((page) => page.PostItems);
        return { ...data, items };
      }, []),
    });

  const { handleRefresh, isUserRefreshing, showHeaderRefetchIndicator, scrollToTop, onScroll } =
    useInfiniteListUtils({
      queryKey: ['notifications'],
      flashListRef,
      pageKey: '/notifications', // Unique key for scroll position persistence and navigation refresh
    });

  const handleSilentRefresh = useCallback(() => {
    return handleRefresh({
      showRefreshingIndicator: false,
      showHeaderRefetchSpinner: false,
    });
  }, [handleRefresh]);

  const hasUnreadNotifications = notificationCount > 0;

  // Refetch when page regains focus because SignalR counts can be stale or zero.
  useEffect(() => {
    const wasScreenActive = wasScreenActiveRef.current;
    wasScreenActiveRef.current = isScreenActive;

    if (isScreenActive && !wasScreenActive && !isScrolled && !isPending && !isUserRefreshing) {
      handleSilentRefresh();
    }
  }, [isScreenActive, isScrolled, isPending, isUserRefreshing, handleSilentRefresh]);

  // Handle SignalR updates for the notifications list
  useEffect(() => {
    if (notificationCount !== lastNotificationCount && isScreenActive && notificationCount > 0) {
      // Only auto-refresh if user is at the top (not scrolled down)
      if (!isScrolled) {
        handleSilentRefresh();
      } else {
        // If user is scrolled down, just update the last notification count
        // so the floating button can appear
        setLastNotificationCount(notificationCount);
      }
    }
  }, [notificationCount, lastNotificationCount, isScreenActive, isScrolled, handleSilentRefresh]);

  // Update last notification count when component mounts or notification count changes
  useEffect(() => {
    setLastNotificationCount(notificationCount);
  }, [notificationCount]);

  const showNewNotificationsButton = isScrolled && hasUnreadNotifications;
  const hasNotifications = !isPending && (data?.items.length ?? 0) > 0;

  const handleScroll = (event: { nativeEvent: { contentOffset: { y: number } } }) => {
    const scrollOffset = event.nativeEvent.contentOffset.y;
    if (scrollOffset > 100) {
      setIsScrolled(true);
    } else {
      setIsScrolled(false);
    }
    onScroll?.(event);
  };

  const handleClearNotifications = async () => {
    if (isClearingNotifications) return;

    try {
      setIsClearingNotifications(true);
      await PostsService.dismissAllNotifications();
      queryClient.setQueryData(['notifications'], (oldData: any) => {
        if (!oldData?.pages) return oldData;

        const pages = oldData.pages.map((page: PostsView) => ({
          ...page,
          PostItems: [],
          NextPageSkip: undefined,
          Cursor: undefined,
          ItemCount: 0,
        }));

        return { ...oldData, pages, items: [] };
      });
      setIsScrolled(false);
      showSuccessToast(t('notifications.clearAllSuccess'), undefined, 'bottom');
    } catch (error) {
      showErrorToast(t('notifications.clearAllError'), error, 'bottom');
    } finally {
      setIsClearingNotifications(false);
      setShowClearConfirmation(false);
    }
  };

  return (
    <VStack className="flex-1">
      <ScreenHeader
        options={{
          hideBackButton: true,
          title: t('notifications.title'),
          ...(isLargeScreen && {
            headerLeft: () =>
              showHeaderRefetchIndicator ? <ActivityIndicator color="grey" /> : null,
          }),
          ...(!isLargeScreen && {
            headerLeft: () => (
              <ButtonWrapper
                icon={Settings01Icon}
                iconSize={20}
                size="sm"
                inAppHeaderBar
                onPress={() => router.push('/account/settings/notifications')}
                accessibilityLabel={t('settings.notifications.title')}
              />
            ),
          }),
          headerRight: () => (
            <HStack className="items-center gap-3">
              {hasNotifications && (
                <ButtonWrapper
                  icon={Delete02Icon}
                  text={isLargeScreen ? t('notifications.clearAll') : undefined}
                  iconSize={20}
                  size="sm"
                  inAppHeaderBar={!isLargeScreen}
                  onPress={() => setShowClearConfirmation(true)}
                  isDisabled={isClearingNotifications}
                  accessibilityLabel={t('notifications.clearAll')}
                />
              )}
              {isLargeScreen && (
                <ButtonWrapper
                  icon={Settings01Icon}
                  iconSize={20}
                  size="sm"
                  onPress={() => router.push('/account/settings/notifications')}
                  accessibilityLabel={t('settings.notifications.title')}
                />
              )}
              <HeaderRefreshButton
                isLargeScreen={isLargeScreen}
                isRefreshing={isUserRefreshing}
                isDisabled={isRefetching}
                inAppHeaderBar={!isLargeScreen}
                onPress={handleRefresh}
              />
            </HStack>
          ),
        }}
      />

      {(() => {
        const skeletonItems = createSkeletonItems(5);
        if (error) {
          return (
            <ErrorInListView error={error} onRetry={handleRefresh} isRetrying={isUserRefreshing} />
          );
        }
        return (
          <Box className="flex-1">
            <FlashList
              ref={flashListRef}
              data={isPending ? skeletonItems : data.items}
              renderItem={({ item }) => (
                <CardWrapper>
                  {isPending ? (
                    <NotificationCard />
                  ) : (
                    <NotificationCard post={item} key={`notification-${item.Id}`} />
                  )}
                </CardWrapper>
              )}
              keyExtractor={(item, index) => (isPending ? `s-${index}` : `notification-${item.Id}`)}
              contentContainerClassName="pb-4"
              onScroll={handleScroll}
              scrollEventThrottle={16}
              onEndReached={() => {
                if (!isPending && hasNextPage && isScreenActive && !isFetchingNextPage) {
                  fetchNextPage();
                }
              }}
              onEndReachedThreshold={onEndReachedThreshold}
              ListEmptyComponent={
                <MessageView
                  type="warning"
                  text={t('notifications.empty')}
                  icon={LicenseNoIcon}
                  listEmpty
                />
              }
              ListFooterComponent={() => {
                if (!isFetchingNextPage) return null;
                return (
                  <Box className="m-4">
                    <ActivityIndicator color="grey" />
                  </Box>
                );
              }}
              refreshControl={
                <RefreshControl refreshing={isUserRefreshing} onRefresh={handleRefresh} />
              }
            />
            <FloatingScrollButton
              isVisible={!!showNewNotificationsButton}
              onPress={async () => {
                scrollToTop(true);
                await handleRefresh();
              }}
              text={t('notifications.newNotifications')}
            />
          </Box>
        );
      })()}

      <AlertDialog
        isOpen={showClearConfirmation}
        onClose={() => setShowClearConfirmation(false)}
        size="md">
        <AlertDialogBackdrop />
        <AlertDialogContent>
          <AlertDialogHeader>
            <Heading size="md">{t('notifications.clearAllConfirmTitle')}</Heading>
            <AlertDialogCloseButton>
              <HugeIconsWrapper icon={Cancel01Icon} size={20} className="color-neutral-500" />
            </AlertDialogCloseButton>
          </AlertDialogHeader>
          <AlertDialogBody className="mt-3">
            <Text>{t('notifications.clearAllConfirmMessage')}</Text>
          </AlertDialogBody>
          <AlertDialogFooter className="mt-6 gap-3">
            <Button
              variant="outline"
              action="neutral"
              onPress={() => setShowClearConfirmation(false)}
              disabled={isClearingNotifications}>
              <ButtonText>{t('common.cancel')}</ButtonText>
            </Button>
            <Button
              action="negative"
              onPress={handleClearNotifications}
              disabled={isClearingNotifications}>
              <ButtonText>{t('common.clear')}</ButtonText>
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </VStack>
  );
}
